import React, { useEffect, useId, useRef, useState } from "react";
import { ImageIcon, Link2, Loader2, RefreshCw, Trash2, Upload } from "lucide-react";
import {
  AppButton, AppModal, DropZone, FieldError, FormField, HelpText, Input, Label,
} from "../ui";
import { BLOG_IMAGE_ACCEPT, uploadBlogImage } from "./blogImages";

const isHttpUrl = (s) => /^https?:\/\/\S+$/i.test(String(s || "").trim());

/**
 * Add an image to a blog post's body, or change one already there.
 *
 * Props:
 *   show
 *   image     — null to add one; `{ src, alt }` to edit the image clicked in the editor
 *   onSubmit  — ({ url, alt }) once the author presses Insert / Save
 *   onRemove  — edit mode only: take the image out of the post
 *   onHide
 *
 * The file is uploaded as soon as it is chosen, so the preview is the copy the
 * website will show.
 */
export default function BlogImageDialog({ show, image, onSubmit, onRemove, onHide }) {
  const editing = Boolean(image);
  const inputRef = useRef(null);
  const id = useId();
  const [src, setSrc] = useState("");
  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [drag, setDrag] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState("");
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSrc(image?.src || "");
    setAlt(image?.alt || "");
    setBusy(false);
    setError("");
    setDrag(false);
    setLinkOpen(false);
    setLinkDraft("");
    setBroken(false);
  }, [show, image]);

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    setError("");
    try {
      const url = await uploadBlogImage(file);
      setSrc(url);
      setBroken(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    upload(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files?.[0]);
  };
  const onDrag = (e) => {
    e.preventDefault();
    if (!busy) setDrag(e.type === "dragenter" || e.type === "dragover");
  };
  const applyLink = () => {
    if (!isHttpUrl(linkDraft)) return;
    setSrc(linkDraft.trim());
    setBroken(false);
    setError("");
    setLinkOpen(false);
  };

  const submit = () => {
    if (!src || busy) return;
    onSubmit({ url: src, alt: alt.trim() });
  };

  const footer = (
    <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
      <div>
        {editing && (
          <AppButton type="button" variant="ghost-destructive" onClick={onRemove} disabled={busy}>
            <Trash2 size={16} aria-hidden="true" /> Remove from post
          </AppButton>
        )}
      </div>
      <div className="tw-flex tw-gap-2">
        <AppButton type="button" variant="outline" onClick={onHide}>Cancel</AppButton>
        <AppButton type="button" onClick={submit} disabled={!src || busy || broken}>
          {editing ? "Save" : "Insert image"}
        </AppButton>
      </div>
    </div>
  );

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title={editing ? "Edit image" : "Add an image"}
      icon={<ImageIcon size={18} aria-hidden="true" />}
      size="md"
      footer={footer}
    >
      <div className="tw-flex tw-flex-col tw-gap-5">
        <input
          ref={inputRef}
          type="file"
          accept={BLOG_IMAGE_ACCEPT}
          onChange={onFile}
          className="tw-hidden"
          tabIndex={-1}
          aria-hidden="true"
        />

        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <Label id={`${id}-label`}>Image</Label>
          {src ? (
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
              {broken ? (
                <p className="tw-m-0 tw-rounded-sm tw-border tw-border-dashed tw-border-warning tw-bg-warning/[0.08] tw-px-3 tw-py-2 tw-text-[0.8125rem] tw-text-warning-fg">
                  This image could not be loaded. Check the link, or upload the file instead.
                </p>
              ) : (
                <img
                  src={src}
                  alt={alt || "Preview of the image"}
                  onError={() => setBroken(true)}
                  className="tw-block tw-max-h-64 tw-max-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface tw-object-contain"
                />
              )}
              <AppButton type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy}>
                {busy
                  ? <Loader2 size={16} className="tw-animate-spin" aria-hidden="true" />
                  : <RefreshCw size={16} aria-hidden="true" />}
                {busy ? "Uploading…" : "Use a different image"}
              </AppButton>
            </div>
          ) : (
            <DropZone
              state={drag ? "active" : "idle"}
              disabled={busy}
              aria-labelledby={`${id}-label`}
              aria-describedby={`${id}-help`}
              onClick={() => inputRef.current?.click()}
              onDragEnter={onDrag}
              onDragOver={onDrag}
              onDragLeave={onDrag}
              onDrop={onDrop}
            >
              {busy
                ? <Loader2 size={22} className="tw-animate-spin tw-text-content-muted" aria-hidden="true" />
                : <Upload size={22} className="tw-text-content-subtle group-hover:tw-text-primary" aria-hidden="true" />}
              <span className="tw-text-sm tw-font-medium tw-text-content">
                {busy ? "Uploading…" : "Upload an image, or drop it here"}
              </span>
            </DropZone>
          )}
          <HelpText id={`${id}-help`}>
            PNG, JPG, WebP or GIF. Large photos are resized for the web before they upload.
          </HelpText>
          {error && <FieldError>{error}</FieldError>}

          {!src && (
            linkOpen ? (
              <div className="tw-flex tw-items-center tw-gap-2">
                <Input
                  aria-label="Image link"
                  value={linkDraft}
                  onChange={(e) => setLinkDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } }}
                  placeholder="https://…"
                />
                <AppButton type="button" variant="outline" size="sm" onClick={applyLink} disabled={!isHttpUrl(linkDraft)}>
                  Use link
                </AppButton>
              </div>
            ) : (
              <div>
                <AppButton type="button" variant="ghost" size="xs" onClick={() => setLinkOpen(true)}>
                  <Link2 size={14} aria-hidden="true" /> Use an image from another website
                </AppButton>
              </div>
            )
          )}
        </div>

        <FormField
          label="Describe the image"
          hint="Read aloud to readers who cannot see it, and read by Google. Leave it empty only for a purely decorative image."
        >
          <Input
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder="e.g. Students building their final project in the Java lab"
            maxLength={250}
          />
        </FormField>
      </div>
    </AppModal>
  );
}
